import _styled, { default as _styled2, default as _styled3, default as _styled4, default as _styled5, default as _styled6, default as _styled7, default as _styled8, default as _styled9, default as _styled10, default as _styled11, default as _styled12, default as _styled13, default as _styled14, default as _styled15, default as _styled16, default as _styled17, default as _styled18 } from "styled-components";
import React from "react";
import { graphql, useFragment, type FragmentType } from "~/gql/runtime";
import { Asset } from "~/features/craft/asset";
import { takeFirst } from "~/features/app/utils";
import { getThemeStyle, type Mode, type Theme } from "~/features/ui/get-theme-style";
import { Link } from "~/features/ui/link";
import { Button } from "~/features/ui/button";
import { RichText } from "~/features/ui/rich-text";
import { Icon } from "~/features/ui/icon";
import ArrowRight from "~/public/icons/arrow-right.svg";
import ArrowTopRight from "~/public/icons/arrow-top-right.svg";
const fragment = graphql(`
  fragment DistributorCard on distributor_default_Entry {
    id
    title
    email
    url: baseUrl
    industry {
      ... on industryCategory_Category {
        title
      }
    }
    regions {
      ... on regionCategory_Category {
        id
        title
        slug
      }
    }
    logo: baseImage {
      ...Asset
    }
    intro: baseRichText
    locations: baseLocations {
      ... on baseLocations_location_BlockType {
        id
        location {
          parts {
            country
            state
          }
          lat
          lng
        }
      }
    }
  }
`);
type Props = FragmentType<typeof fragment> & {
  mode: Mode;
  theme: Theme;
  highlighted?: boolean;
} & React.ComponentPropsWithoutRef<"div">;

/**
 * The distributors block spreads a whole distributor card object into this
 * component, so `rest` carries the fragment's own fields next to the genuine div
 * props (`onClick`, `data-*`, …). Only the latter may reach the DOM: spreading
 * the fragment fields rendered `industry="[object Object]"` and the entire intro
 * HTML as attributes, and tripped React's unknown-prop warning.
 */
const FRAGMENT_FIELDS = new Set(["id", "title", "email", "url", "industry", "regions", "logo", "intro", "locations"]);
function toDivProps(props: object) {
  return Object.fromEntries(Object.entries(props).filter(([key]) => !FRAGMENT_FIELDS.has(key))) as React.ComponentPropsWithoutRef<"div">;
}
export const DistributorCard = React.forwardRef<HTMLDivElement, Props>((props, ref) => {
  const {
    theme,
    mode,
    highlighted,
    ...rest
  } = props;
  const {
    title,
    url,
    regions,
    locations,
    industry,
    email,
    logo: _logo,
    intro
  } = useFragment(fragment, rest);
  const themeStyle = getThemeStyle({
    mode,
    theme
  });
  const logo = takeFirst(_logo);
  return <_StyledDiv ref={ref} {...toDivProps(rest)} $_css={[{
    "position": "relative",
    "zIndex": "10",
    "display": "flex",
    "height": "100%",
    "minWidth": "28.8rem",
    "cursor": "pointer",
    "flexDirection": "column",
    "rowGap": "2.4rem",
    "padding": "2.4rem",
    "@media (min-width: 1024px)": {
      "padding": "4rem"
    }
  }, themeStyle.background, highlighted && {
    "--tw-bg-opacity": "1",
    "backgroundColor": "rgb(227 227 240 / var(--tw-bg-opacity))"
  }]}>
      {logo ? <_StyledAsset asset={logo} imageProps={{
      sizes: "lg:33vw"
    }} /> : <_StyledDiv2 />}

      {title && <_StyledSpan>{title}</_StyledSpan>}
      <div>
        {regions.length > 0 && <_StyledDiv3 $_css2={[{
        "display": "flex",
        "alignItems": "center",
        "gap": "0.8rem",
        "borderBottomWidth": "1px",
        "paddingTop": "0.8rem",
        "paddingBottom": "0.8rem"
      }, themeStyle.border]}>
            <_StyledP>Regions Served</_StyledP>
            <_StyledP2>
              {regions.flatMap(region => region.title).join(", ")}
            </_StyledP2>
          </_StyledDiv3>}
        {locations && locations.length > 0 && <_StyledDiv4 $_css3={[{
        "display": "flex",
        "alignItems": "center",
        "gap": "0.8rem",
        "borderBottomWidth": "1px",
        "paddingTop": "0.8rem",
        "paddingBottom": "0.8rem"
      }, themeStyle.border]}>
            <_StyledP3>Countries</_StyledP3>
            <_StyledP4>
              {locations.map(l => l.location?.parts?.country).join(", ")}
            </_StyledP4>
          </_StyledDiv4>}
        {industry && industry.length > 0 && <_StyledDiv5 $_css4={[{
        "display": "flex",
        "alignItems": "center",
        "gap": "0.8rem",
        "borderBottomWidth": "1px",
        "paddingTop": "0.8rem",
        "paddingBottom": "0.8rem"
      }, themeStyle.border]}>
            <_StyledP5>Industry</_StyledP5>
            <_StyledP6>
              {industry[0]?.title}
            </_StyledP6>
          </_StyledDiv5>}
        {email && <_StyledDiv6 $_css5={[{
        "display": "flex",
        "alignItems": "center",
        "gap": "0.8rem",
        "borderBottomWidth": "1px",
        "paddingTop": "0.8rem",
        "paddingBottom": "0.8rem"
      }, themeStyle.border]}>
            <_StyledP7>Email</_StyledP7>
            <_StyledP8>
              {email}
            </_StyledP8>
          </_StyledDiv6>}
      </div>
      {intro && <_StyledRichText text={intro} />}
      <_StyledDiv7>
        {url && <Button as={Link} variant="underline" icon={ArrowRight} href={url} target="_blank" rel="noopener">
            Visit Website
          </Button>}

        {email && <Button as={Link} variant="secondary" icon={ArrowTopRight} href={`mailto:${email}`} target="_blank" rel="noopener">
            Contact
          </Button>}
      </_StyledDiv7>
    </_StyledDiv>;
});
var _StyledDiv = /*#__PURE__*/_styled("div").withConfig({
  componentId: "sc-sf-1ueegqb-0"
})`${p => p.$_css}`;
var _StyledAsset = _styled2(Asset).withConfig({
  componentId: "sc-sf-1ueegqb-1"
})({
  "height": "6.8rem",
  "width": "10rem",
  "borderRadius": "4px",
  ">img": {
    "height": "6.8rem"
  }
});
var _StyledDiv2 = _styled3("div").withConfig({
  componentId: "sc-sf-1ueegqb-2"
})({
  "aspectRatio": "16/9",
  "height": "6.8rem",
  "width": "10rem",
  "@media (min-width: 1024px)": {
    "display": "none"
  }
});
var _StyledSpan = _styled4("span").withConfig({
  componentId: "sc-sf-1ueegqb-3"
})({
  "fontSize": "3.2rem",
  "lineHeight": "1.2",
  "letterSpacing": "0.032rem",
  "fontWeight": "400"
});
var _StyledDiv3 = _styled5("div").withConfig({
  componentId: "sc-sf-1ueegqb-4"
})`${p => p.$_css2}`;
var _StyledP = _styled6("p").withConfig({
  componentId: "sc-sf-1ueegqb-5"
})({
  "fontSize": "1.4rem",
  "lineHeight": "1.5",
  "letterSpacing": "0.014000000000000002rem",
  "fontWeight": "500"
});
var _StyledP2 = _styled7("p").withConfig({
  componentId: "sc-sf-1ueegqb-6"
})({
  "fontFamily": "Aeonik, ui-serif, Georgia, Cambria, \"Times New Roman\", Times, serif",
  "fontSize": "1.2rem",
  "lineHeight": "1.1",
  "letterSpacing": "0.024rem",
  "fontWeight": "400",
  "textTransform": "uppercase",
  "--tw-text-opacity": "1",
  "color": "rgb(92 92 92 / var(--tw-text-opacity))",
  "@media (min-width: 1024px)": {
    "fontSize": "1.4rem",
    "lineHeight": "1.1",
    "letterSpacing": "0.028000000000000004rem",
    "fontWeight": "400"
  }
});
var _StyledDiv4 = _styled8("div").withConfig({
  componentId: "sc-sf-1ueegqb-7"
})`${p => p.$_css3}`;
var _StyledP3 = _styled9("p").withConfig({
  componentId: "sc-sf-1ueegqb-8"
})({
  "fontSize": "1.4rem",
  "lineHeight": "1.5",
  "letterSpacing": "0.014000000000000002rem",
  "fontWeight": "500"
});
var _StyledP4 = _styled10("p").withConfig({
  componentId: "sc-sf-1ueegqb-9"
})({
  "fontFamily": "Aeonik, ui-serif, Georgia, Cambria, \"Times New Roman\", Times, serif",
  "fontSize": "1.2rem",
  "lineHeight": "1.1",
  "letterSpacing": "0.024rem",
  "fontWeight": "400",
  "--tw-text-opacity": "1",
  "color": "rgb(92 92 92 / var(--tw-text-opacity))",
  "@media (min-width: 1024px)": {
    "fontSize": "1.4rem",
    "lineHeight": "1.1",
    "letterSpacing": "0.028000000000000004rem",
    "fontWeight": "400"
  }
});
var _StyledDiv5 = _styled11("div").withConfig({
  componentId: "sc-sf-1ueegqb-10"
})`${p => p.$_css4}`;
var _StyledP5 = _styled12("p").withConfig({
  componentId: "sc-sf-1ueegqb-11"
})({
  "fontSize": "1.4rem",
  "lineHeight": "1.5",
  "letterSpacing": "0.014000000000000002rem",
  "fontWeight": "500"
});
var _StyledP6 = _styled13("p").withConfig({
  componentId: "sc-sf-1ueegqb-12"
})({
  "fontFamily": "Aeonik, ui-serif, Georgia, Cambria, \"Times New Roman\", Times, serif",
  "fontSize": "1.2rem",
  "lineHeight": "1.1",
  "letterSpacing": "0.024rem",
  "fontWeight": "400",
  "--tw-text-opacity": "1",
  "color": "rgb(92 92 92 / var(--tw-text-opacity))",
  "@media (min-width: 1024px)": {
    "fontSize": "1.4rem",
    "lineHeight": "1.1",
    "letterSpacing": "0.028000000000000004rem",
    "fontWeight": "400"
  }
});
var _StyledDiv6 = _styled14("div").withConfig({
  componentId: "sc-sf-1ueegqb-13"
})`${p => p.$_css5}`;
var _StyledP7 = _styled15("p").withConfig({
  componentId: "sc-sf-1ueegqb-14"
})({
  "fontSize": "1.4rem",
  "lineHeight": "1.5",
  "letterSpacing": "0.014000000000000002rem",
  "fontWeight": "500"
});
var _StyledP8 = _styled16("p").withConfig({
  componentId: "sc-sf-1ueegqb-15"
})({
  "fontFamily": "Aeonik, ui-serif, Georgia, Cambria, \"Times New Roman\", Times, serif",
  "fontSize": "1.2rem",
  "lineHeight": "1.1",
  "letterSpacing": "0.024rem",
  "fontWeight": "400",
  "--tw-text-opacity": "1",
  "color": "rgb(92 92 92 / var(--tw-text-opacity))",
  "@media (min-width: 1024px)": {
    "fontSize": "1.4rem",
    "lineHeight": "1.1",
    "letterSpacing": "0.028000000000000004rem",
    "fontWeight": "400"
  }
});
var _StyledRichText = _styled17(RichText).withConfig({
  componentId: "sc-sf-1ueegqb-16"
})({
  "fontSize": "1.4rem",
  "lineHeight": "1.5",
  "letterSpacing": "0.014000000000000002rem",
  "fontWeight": "400",
  "@media (min-width: 1024px)": {
    "fontSize": "1.4rem",
    "lineHeight": "1.5",
    "letterSpacing": "0.014000000000000002rem",
    "fontWeight": "400"
  }
});
var _StyledDiv7 = /*#__PURE__*/_styled18("div").withConfig({
  displayName: "distributor-card___StyledDiv7",
  componentId: "sc-1ijtnt7-0"
})({
  "display": "flex",
  "alignItems": "center",
  "gap": "2.4rem"
});